<template>
  <div class="flex flex-col flex-1 overflow-y-auto" style="zoom: 0.9">
    <div class="flex justify-between items-center px-4 pb-4 border-b">
      <div class="flex flex-1 grid grid-cols-3 ">
        <div class="flex justify-center items-center gap-2">
          <v-icon class="text-gray-400" size="32"
            >mdi-file-chart-outline</v-icon
          >
          <div class="flex flex-col justify-center items-center">
            <p class="text-lg font-bold">{{ csatReport?.total }}</p>
            <p class="text-xs text-gray-500">Total Surveys</p>
          </div>
        </div>
        <div class="flex justify-center items-center gap-2">
          <v-icon class="text-green-500/50" size="32"
            >mdi-check-circle-outline</v-icon
          >
          <div class="flex flex-col justify-center items-center">
            <p class="text-lg font-bold">{{ csatReport?.complete }}</p>
            <p class="text-xs text-gray-500">Completed Surveys</p>
          </div>
        </div>
        <div class="flex justify-center items-center gap-2">
          <v-icon class="text-red-500/50" size="32"
            >mdi-close-circle-outline</v-icon
          >
          <div class="flex flex-col justify-center items-center">
            <p class="text-lg font-bold">
              {{ csatReport?.not_complete }}
            </p>
            <p class="text-xs text-gray-500">Uncompleted Surveys</p>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col flex-1 grid grid-cols-2 overflow-y-auto">
      <div
        class="flex flex-1 flex-col items-center border py-2 gap-2"
        v-for="(question, index) in sortedRatings"
        :key="question?.id"
      >
        <h2 class="text-sm font-semibold">
          {{ csatQuestions?.find((q) => q.id === question.id)?.message }}
        </h2>
        <div
          v-if="question.quest_type === 1 || question.quest_type === 4"
          class="flex flex-col flex-1 gap-2 p-2"
        >
          <div
            class="grid bg-primary-500 rounded-t-lg"
            :class="question.quest_type === 4 ? 'grid-cols-3' : 'grid-cols-5'"
          >
            <div
              class="flex flex-col justify-center items-center"
              v-for="(rating, index) in questionLabels.find(
                (l) => l.type === question.quest_type
              ).labels"
              :key="index"
            >
              <p class="text-xs text-white m-2">
                {{ $t(`reports.csat_report.${rating}`) }}
              </p>
              <div
                class="text-xs w-full text-center p-1 font-bold"
                :style="{
                  backgroundColor: getRatingColor(rating),
                }"
              >
                {{ question.ratings[rating].result }}%
              </div>
            </div>
          </div>
          <div class="flex justify-center items-center gap-2">
            <div
              class="flex justify-center items-center gap-2"
              v-for="(rating, index) in questionLabels.find(
                (l) => l.type === question.quest_type
              ).labels"
              :key="index"
            >
              <div
                :style="{ backgroundColor: getRatingColor(rating) }"
                class="w-3 h-3 rounded-full"
              ></div>
              <p class="text-xs">{{ $t(`reports.csat_report.${rating}`) }}</p>
            </div>
          </div>
          <div class="flex items-center justify-center gap-12">
            <Chart
              type="bar"
              :data="getChartData(question)"
              :options="barChartsOptions[index]"
              class="w-3/6 h-full self-center"
            />
            <div class="flex flex-col items-center gap-2">
              <img
                :src="require('@/assets/satisfied.jpg')"
                alt="Logo"
                class="w-16 h-16"
              />
              <p class="text-sm">{{ question.result }}%</p>
            </div>
          </div>
        </div>
        <div
          v-else-if="question.quest_type === 2"
          class="flex flex-col flex-1 grid grid-cols-2 gap-8 justify-center items-center"
        >
          <div class="flex flex-col flex-1 h-full">
            <div class="flex justify-center items-center gap-2">
              <div
                class="flex justify-center items-center gap-2"
                v-for="(rating, index) in questionLabels.find(
                  (l) => l.type === question.quest_type
                ).labels"
                :key="index"
              >
                <div
                  :style="{ backgroundColor: getRatingColor(rating) }"
                  class="w-3 h-3 rounded-full"
                ></div>
                <p class="text-xs">{{ $t(`reports.csat_report.${rating}`) }}</p>
              </div>
            </div>
            <Chart
              type="bar"
              :data="getChartData(question)"
              :options="barChartsOptions[index]"
              class="w-3/6 h-full self-center"
              :height="250"
              :width="200"
            />
          </div>
          <div class="flex flex-col flex-1 h-full gap-8">
            <div class="flex justify-center items-center gap-2">
              <div
                class="flex justify-center items-center gap-2"
                v-for="(rating, index) in questionLabels.find(
                  (l) => l.type === question.quest_type
                ).labels"
                :key="index"
              >
                <div
                  :style="{ backgroundColor: getRatingColor(rating) }"
                  class="w-3 h-3 rounded-full"
                ></div>
                <p class="text-xs">{{ $t(`reports.csat_report.${rating}`) }}</p>
              </div>
            </div>
            <Chart
              type="pie"
              :data="getPercentageChartData(question)"
              :options="chartOptions"
              class="w-full h-4/6"
              :height="200"
            />
          </div>
        </div>
        <!-- NPS Type 3 -->
        <div
          v-else-if="question.quest_type === 3"
          class="flex flex-col flex-1 gap-2 p-2"
        >
          <div class="grid grid-cols-3 bg-primary-500 rounded-t-lg">
            <div
              class="flex flex-col justify-center items-center"
              v-for="(rating, rIdx) in questionLabels.find(
                (l) => l.type === question.quest_type
              ).labels"
              :key="rIdx"
            >
              <p class="text-xs text-white m-2">
                {{ $t(`reports.csat_report.${rating === 'high' ? 'promoters' : rating === 'mid' ? 'passives' : 'detractors'}`) }}
              </p>
              <div
                class="text-xs w-full text-center p-1 font-bold"
                :style="{
                  backgroundColor: getRatingColor(rating),
                }"
              >
                {{ question.ratings[rating].result }}%
              </div>
            </div>
          </div>
          <div class="flex justify-center items-center gap-2">
            <div
              class="flex justify-center items-center gap-2"
              v-for="(rating, rIdx) in questionLabels.find(
                (l) => l.type === question.quest_type
              ).labels"
              :key="rIdx"
            >
              <div
                :style="{ backgroundColor: getRatingColor(rating) }"
                class="w-3 h-3 rounded-full"
              ></div>
              <p class="text-xs">{{ $t(`reports.csat_report.${rating === 'high' ? 'promoters' : rating === 'mid' ? 'passives' : 'detractors'}`) }}</p>
            </div>
          </div>
          <div class="flex items-center justify-center gap-12">
            <Chart
              type="bar"
              :data="getChartData(question)"
              :options="barChartsOptions[index]"
              class="h-full self-center"
            />
             <div class="flex flex-col items-center gap-2">
              <img
                :src="require('@/assets/satisfied.jpg')"
                alt="Logo"
                class="w-16 h-16"
              />
              <p class="text-sm">{{ question.result }}%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Chart from "primevue/chart";
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    Chart,
  },
  props: {
    csatReport: {
      required: true,
    },
  },
  computed: {
    ...mapGetters(["csatQuestions"]),
    sortedRatings() {
      if (!this.csatReport?.ratings) return [];
      return [...this.csatReport.ratings].sort((a, b) => {
        const orderA = this.csatQuestions.find((q) => q.id === a.id)?.quest_order ?? 0;
        const orderB = this.csatQuestions.find((q) => q.id === b.id)?.quest_order ?? 0;
        if (orderA === 0 && orderB === 0) return 0;
        if (orderA === 0) return 1;
        if (orderB === 0) return -1;
        return orderA - orderB;
      });
    },
  },
  data() {
    return {
      questionLabels: [
        {
          type: 1,
          labels: [
            "very_satisfied",
            "satisfied",
            "neutural",
            "dissatisfied",
            "very_dissatisfied",
          ],
        },
        { type: 2, labels: ["yes", "no"] },
        { type: 3, labels: ["high", "mid", "low"] },
        { type: 4, labels: ["satisfied", "neutural", "dissatisfied"] },
      ],
      maxYValues: [],
      chartOptions: {
        responsive: false,
        maintainAspectRatio: false,
        categoryPercentage: 1,
        barPercentage: 1,
        layout: {
          padding: 8,
        },
        plugins: {
          legend: {
            display: false,
            labels: {
              usePointStyle: true,
              pointStyle: "circle",
              pointRadius: 5,
              pointHoverRadius: 7,
              pointBackgroundColor: "rgba(0, 0, 0, 0.5)",
              pointBorderColor: "rgba(0, 0, 0, 0.5)",
              pointBorderWidth: 2,
              pointHoverBorderWidth: 2,
            },
          },
          datalabels: {
            display: true,
            color: "#000",
            anchor: "end",
            align: "end",
            formatter: (value) => `${value}%`,
            font: {
              weight: "bold",
            },
          },
        },
        scales: {
          x: {
            display: false,
          },
          y: {
            display: false,
          },
        },
      },
      barChartOptions: {
        responsive: false,
        maintainAspectRatio: false,
        categoryPercentage: 1,
        barPercentage: 1,
        plugins: {
          legend: {
            display: false,
          },
          datalabels: {
            display: true,
            color: "#000",
            anchor: "end",
            align: "end",
            offset: 4,
          },
        },
        scales: {
          x: {
            display: false,
          },
          y: {
            display: true,
            beginAtZero: false,
            ticks: {
              display: true,
              color: "transparent",
            },
            grid: {
              display: false,
            },
            border: {
              display: false,
            },
          },
        },
      },
      barChartsOptions: [],
    };
  },
  methods: {
    getRatingColor(rating) {
      switch (rating) {
        case "very_satisfied":
        case "yes":
        case "high":
          return "limegreen";
        case "satisfied":
          return "green";
        case "neutral":
        case "neutural":
          return "yellow";
        case "mid":
          return "orange";
        case "dissatisfied":
          return "crimson";
        case "very_dissatisfied":
        case "no":
        case "low":
          return "red";
        default:
          return "gray";
      }
    },
    getChartData(question) {
      let labels, data, backgroundColor;

      if (question.quest_type === 1) {
        labels = [
          this.$t("reports.csat_report.very_satisfied"),
          this.$t("reports.csat_report.satisfied"),
          this.$t("reports.csat_report.neutral"),
          this.$t("reports.csat_report.dissatisfied"),
          this.$t("reports.csat_report.very_dissatisfied"),
        ];
        data = [
          question.ratings.very_satisfied.count,
          question.ratings.satisfied.count,
          question.ratings.neutural.count,
          question.ratings.dissatisfied.count,
          question.ratings.very_dissatisfied.count,
        ];
        backgroundColor = ["limegreen", "green", "yellow", "crimson", "red"];
      } else if (question.quest_type === 4) {
        labels = [
          this.$t("reports.csat_report.satisfied"),
          this.$t("reports.csat_report.neutral"),
          this.$t("reports.csat_report.dissatisfied"),
        ];
        data = [
          question.ratings.satisfied.count,
          question.ratings.neutural.count,
          question.ratings.dissatisfied.count,
        ];
        backgroundColor = ["green", "yellow", "crimson"];
      } else if (question.quest_type === 3) {
        labels = [
          this.$t("reports.csat_report.promoters"),
          this.$t("reports.csat_report.passives"),
          this.$t("reports.csat_report.detractors"),
        ];
        data = [
          question.ratings.high.count,
          question.ratings.mid.count,
          question.ratings.low.count,
        ];
        backgroundColor = ["limegreen", "orange", "red"];
      } else {
        labels = [
          this.$t("reports.csat_report.yes"),
          this.$t("reports.csat_report.no"),
        ];
        data = [question.ratings.yes.count, question.ratings.no.count];
        backgroundColor = ["limegreen", "red"];
      }

      return {
        labels,
        datasets: [{ data, backgroundColor }],
      };
    },
    getPercentageChartData(question) {
      const keys = Object.keys(question.ratings);
      const colors = keys.map((key) => this.getRatingColor(key));
      return {
        labels: keys,
        datasets: [
          {
            data: Object.values(question.ratings).map(
              (rating) => rating.result
            ),
            backgroundColor: colors,
          },
        ],
      };
    },
    ...mapActions(["GetCSATQuestions"]),
  },
  watch: {
    csatReport(newValue) {
      this.maxYValues = newValue?.ratings?.map(
        (question) =>
          Math.max(
            ...Object.values(question.ratings).map((rating) => rating.count)
          ) + 20
      );
      this.barChartsOptions = newValue?.ratings?.map((_, index) => {
        return {
          ...this.barChartOptions,
          scales: {
            ...this.barChartOptions.scales,
            y: {
              ...this.barChartOptions.scales.y,
              max: this.maxYValues[index],
            },
          },
        };
      });
    },
  },
  async created() {
    if (this.csatQuestions.length === 0) {
      await this.GetCSATQuestions();
    }
    this.maxYValues = this.csatReport?.ratings?.map(
      (question) =>
        Math.max(
          ...Object.values(question.ratings).map((rating) => rating.count)
        ) + 20
    );
    this.barChartsOptions = this.csatReport?.ratings?.map((_, index) => {
      return {
        ...this.barChartOptions,
        scales: {
          ...this.barChartOptions.scales,
          y: {
            ...this.barChartOptions.scales.y,
            max: this.maxYValues[index],
          },
        },
      };
    });
  },
};
</script>

<style></style>
